import { ListFilter } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type {
  ProductClicksTimeSeriesPeriod,
  TimeSeriesPeriod,
} from '@/types/analytics'

interface DataOverTimeChartProps {
  widgetLoads: TimeSeriesPeriod[]
  widgetViews: TimeSeriesPeriod[]
  sessions: TimeSeriesPeriod[]
  productClicks: ProductClicksTimeSeriesPeriod[]
  dateRangeLabel?: string
  isFullScreen?: boolean
}

type MetricKey =
  | 'widgetLoads'
  | 'widgetViews'
  | 'sessions'
  | 'recommendations'
  | 'welcomeProducts'
  | 'fallbackProducts'
  | 'productCards'

const metricOptions: { key: MetricKey; label: string }[] = [
  { key: 'widgetLoads', label: 'Loads' },
  { key: 'widgetViews', label: 'Views' },
  { key: 'sessions', label: 'Engaged' },
  { key: 'recommendations', label: 'Redirects' },
  { key: 'welcomeProducts', label: 'Welcome Clicks' },
  { key: 'fallbackProducts', label: 'Fallback Clicks' },
  { key: 'productCards', label: 'Pinned Clicks' },
]

const defaultVisibleMetrics = new Set<MetricKey>([
  'widgetLoads',
  'widgetViews',
  'sessions',
  'recommendations',
  'welcomeProducts',
  'fallbackProducts',
  'productCards',
])

const chartConfig = {
  widgetLoads: {
    label: 'Loads',
    color: 'hsl(var(--chart-1))',
  },
  widgetViews: {
    label: 'Views',
    color: 'hsl(var(--chart-2))',
  },
  sessions: {
    label: 'Engaged',
    color: 'hsl(var(--chart-3))',
  },
  recommendations: {
    label: 'Recommendation Clicks',
    color: 'hsl(var(--chart-4))',
  },
  welcomeProducts: {
    label: 'Welcome Product Clicks',
    color: 'hsl(var(--chart-5))',
  },
  fallbackProducts: {
    label: 'Fallback Product Clicks',
    color: 'hsl(var(--chart-6))',
  },
  productCards: {
    label: 'Pinned Product Clicks',
    color: 'hsl(var(--chart-7))',
  },
} satisfies ChartConfig

const chartClassNames = {
  card: 'flex-1 rounded-2xl shadow-sm',
  header: 'pb-2',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0',
}

export function DataOverTimeChart({
  widgetLoads,
  widgetViews,
  sessions,
  productClicks,
  dateRangeLabel,
  isFullScreen = false,
}: DataOverTimeChartProps) {
  const resolvedMetricOptions = useMemo(() => {
    if (isFullScreen) {
      return metricOptions
        .filter((opt) => opt.key !== 'widgetViews')
        .map((opt) =>
          opt.key === 'widgetLoads' ? { ...opt, label: 'Views' } : opt
        )
    }
    return metricOptions
  }, [isFullScreen])

  const resolvedChartConfig = useMemo(() => {
    if (isFullScreen) {
      const { widgetViews: _, ...rest } = chartConfig
      return {
        ...rest,
        widgetLoads: { ...rest.widgetLoads, label: 'Views' },
      } satisfies ChartConfig
    }
    return chartConfig
  }, [isFullScreen])

  const resolvedDefaultMetrics = useMemo(() => {
    if (isFullScreen) {
      const metrics = new Set(defaultVisibleMetrics)
      metrics.delete('widgetViews')
      return metrics
    }
    return defaultVisibleMetrics
  }, [isFullScreen])

  const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricKey>>(
    resolvedDefaultMetrics
  )

  useEffect(() => {
    setVisibleMetrics(resolvedDefaultMetrics)
  }, [resolvedDefaultMetrics])

  const toggleMetric = (metric: MetricKey) => {
    setVisibleMetrics((prev) => {
      const next = new Set(prev)
      if (next.has(metric)) {
        next.delete(metric)
      } else {
        next.add(metric)
      }
      return next
    })
  }

  // Filter chart config based on visible metrics
  const filteredChartConfig = useMemo(() => {
    return Object.fromEntries(
      Object.entries(resolvedChartConfig).filter(([key]) =>
        visibleMetrics.has(key as MetricKey)
      )
    ) as ChartConfig
  }, [visibleMetrics, resolvedChartConfig])

  // Merge all data series by period
  const chartData = useMemo(() => {
    const dataByPeriod = new Map<
      string,
      {
        period: string
        label: string
        widgetLoads: number
        widgetViews: number
        sessions: number
        recommendations: number
        welcomeProducts: number
        fallbackProducts: number
        productCards: number
      }
    >()

    const getOrCreate = (period: string) => {
      if (!dataByPeriod.has(period)) {
        dataByPeriod.set(period, {
          period,
          label: formatPeriodLabel(period),
          widgetLoads: 0,
          widgetViews: 0,
          sessions: 0,
          recommendations: 0,
          welcomeProducts: 0,
          fallbackProducts: 0,
          productCards: 0,
        })
      }

      return dataByPeriod.get(period)!
    }

    // Add widget loads data
    widgetLoads.forEach((item) => {
      getOrCreate(item.period).widgetLoads = item.count
    })

    // Add widget views data
    widgetViews.forEach((item) => {
      getOrCreate(item.period).widgetViews = item.count
    })

    // Add sessions data
    sessions.forEach((item) => {
      getOrCreate(item.period).sessions = item.count
    })

    // Merge product clicks data
    productClicks.forEach((item) => {
      const data = getOrCreate(item.period)
      data.recommendations = item.recommendations
      data.welcomeProducts = item.welcomeProducts
      data.fallbackProducts = item.fallbackProducts
      data.productCards = item.productCards
    })

    // Sort by period and return as array
    return Array.from(dataByPeriod.values()).sort((a, b) =>
      a.period.localeCompare(b.period)
    )
  }, [widgetLoads, widgetViews, sessions, productClicks])

  return (
    <Card className={chartClassNames.card}>
      <CardHeader className={chartClassNames.header}>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle className={chartClassNames.title}>
              Data Over Time
            </CardTitle>
            {dateRangeLabel && (
              <p className={chartClassNames.subtitle}>{dateRangeLabel}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
              >
                <ListFilter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {resolvedMetricOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.key}
                  checked={visibleMetrics.has(option.key)}
                  onCheckedChange={() => toggleMetric(option.key)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className={chartClassNames.content}>
        <ChartContainer
          config={filteredChartConfig}
          className="h-[250px] w-full"
        >
          <LineChart data={chartData} accessibilityLayer>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend
              content={({ payload, verticalAlign }) => (
                <ChartLegendContent
                  payload={payload ? [...payload] : undefined}
                  verticalAlign={verticalAlign}
                />
              )}
            />
            {visibleMetrics.has('widgetLoads') && (
              <Line
                type="monotone"
                dataKey="widgetLoads"
                stroke="var(--color-widgetLoads)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-widgetLoads)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {!isFullScreen && visibleMetrics.has('widgetViews') && (
              <Line
                type="monotone"
                dataKey="widgetViews"
                stroke="var(--color-widgetViews)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-widgetViews)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {visibleMetrics.has('sessions') && (
              <Line
                type="monotone"
                dataKey="sessions"
                stroke="var(--color-sessions)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-sessions)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {visibleMetrics.has('recommendations') && (
              <Line
                type="monotone"
                dataKey="recommendations"
                stroke="var(--color-recommendations)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-recommendations)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {visibleMetrics.has('welcomeProducts') && (
              <Line
                type="monotone"
                dataKey="welcomeProducts"
                stroke="var(--color-welcomeProducts)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-welcomeProducts)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {visibleMetrics.has('fallbackProducts') && (
              <Line
                type="monotone"
                dataKey="fallbackProducts"
                stroke="var(--color-fallbackProducts)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-fallbackProducts)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
            {visibleMetrics.has('productCards') && (
              <Line
                type="monotone"
                dataKey="productCards"
                stroke="var(--color-productCards)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-productCards)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            )}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export function formatPeriodLabel(period: string): string {
  // Handle different period formats
  if (period.includes('-W')) {
    // Weekly format: "2025-W01" -> "Jan 6" (start of week)
    const match = period.match(/^(\d{4})-W(\d{2})$/)
    if (match) {
      const year = Number(match[1])
      const week = Number(match[2])

      // Validate year and week are in reasonable bounds
      const currentYear = new Date().getFullYear()

      if (year < 2000 || year > currentYear + 10 || week < 1 || week > 53) {
        return period // Return raw period if invalid
      }

      // Calculate the Monday of the given ISO week
      const jan4 = new Date(year, 0, 4)
      const dayOfWeek = jan4.getDay() || 7
      const firstMonday = new Date(jan4)
      firstMonday.setDate(jan4.getDate() - dayOfWeek + 1)
      const weekStart = new Date(firstMonday)
      weekStart.setDate(firstMonday.getDate() + (week - 1) * 7)

      // Verify the resulting date is valid
      if (Number.isNaN(weekStart.getTime())) {
        return period
      }

      return weekStart.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }
    return period
  }
  if (period.match(/^\d{4}-\d{2}$/)) {
    // Monthly format: "2025-01" -> "Jan"
    const [year, month] = period.split('-')
    const date = new Date(Number(year), Number(month) - 1)
    return date.toLocaleDateString('en-US', { month: 'short' })
  }
  if (period.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Construct in local time so the date doesn't shift for users west of UTC
    // (new Date("2025-01-15") parses as UTC midnight → renders as Jan 14 in EDT).
    const [year, month, day] = period.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return period
}
